import { t } from './i18n'
import type { MessageKey } from './i18n'

/**
 * Domain codes as a person reads them.
 *
 * Every map falls back to the raw code rather than to a friendly placeholder. A
 * status the server knows and this build does not must still be legible: a draft
 * showing `superseded_by_human` is ugly, and a draft showing "неизвестно" is a
 * draft somebody approves without knowing what it is.
 */
function label(map: Record<string, MessageKey>, code: string): string {
  const key = map[code]
  return key === undefined ? code : t(key)
}

const THREAD_MODES: Record<string, MessageKey> = {
  ai_active: 'mode.ai_active',
  human_paused: 'mode.human_paused',
  handoff_pending: 'mode.handoff_pending',
  blocked: 'mode.blocked',
  closed: 'mode.closed',
}

export function threadModeLabel(mode: string): string {
  return label(THREAD_MODES, mode)
}

const DIRECTIONS: Record<string, MessageKey> = {
  client_inbound: 'direction.client_inbound',
  manual_outbound: 'direction.manual_outbound',
  bot_outbound: 'direction.bot_outbound',
}

export function directionLabel(direction: string): string {
  return label(DIRECTIONS, direction)
}

const DRAFT_STATUSES: Record<string, MessageKey> = {
  pending: 'drafts.status.pending',
  approved: 'drafts.status.approved',
  sending: 'drafts.status.sending',
  sent: 'drafts.status.sent',
  failed: 'drafts.status.failed',
  unknown: 'drafts.status.unknown',
  rejected: 'drafts.status.rejected',
  stale: 'drafts.status.stale',
  superseded_by_human: 'drafts.status.superseded_by_human',
  shadow: 'drafts.status.shadow',
  blocked: 'drafts.status.blocked',
}

export function draftStatusLabel(status: string): string {
  return label(DRAFT_STATUSES, status)
}

const DRAFT_STATUS_HINTS: Record<string, MessageKey> = {
  unknown: 'drafts.statusHint.unknown',
  stale: 'drafts.statusHint.stale',
  superseded_by_human: 'drafts.statusHint.superseded_by_human',
  shadow: 'drafts.statusHint.shadow',
  blocked: 'drafts.statusHint.blocked',
}

/** The sentence under a status, for the statuses whose name is not enough. */
export function draftStatusHint(status: string): string | undefined {
  const key = DRAFT_STATUS_HINTS[status]
  return key === undefined ? undefined : t(key)
}

const DRAFT_KINDS: Record<string, MessageKey> = {
  reply: 'drafts.kind.reply',
  listing_card: 'drafts.kind.listing_card',
  reminder: 'drafts.kind.reminder',
  handoff_ack: 'drafts.kind.handoff_ack',
}

export function draftKindLabel(kind: string): string {
  return label(DRAFT_KINDS, kind)
}

const SEARCH_STATUSES: Record<string, MessageKey> = {
  new: 'search.statusValue.new',
  qualifying: 'search.statusValue.qualifying',
  qualified: 'search.statusValue.qualified',
  matching: 'search.statusValue.matching',
  options_ready: 'search.statusValue.options_ready',
  options_sent: 'search.statusValue.options_sent',
  interested: 'search.statusValue.interested',
  viewing: 'search.statusValue.viewing',
  decision_pending: 'search.statusValue.decision_pending',
  targeted_search: 'search.statusValue.targeted_search',
  paused: 'search.statusValue.paused',
  won: 'search.statusValue.won',
  lost: 'search.statusValue.lost',
  closed: 'search.statusValue.closed',
  spam: 'search.statusValue.spam',
  handoff: 'search.statusValue.handoff',
  no_match: 'search.statusValue.no_match',
  group_candidate: 'search.statusValue.group_candidate',
}

export function searchStatusLabel(status: string): string {
  return label(SEARCH_STATUSES, status)
}

const FIELDS: Record<string, MessageKey> = {
  unit_type: 'search.field.unit_type',
  preferred_unit_type: 'search.field.preferred_unit_type',
  start_date: 'search.field.start_date',
  start_date_earliest: 'search.field.start_date_earliest',
  start_date_latest: 'search.field.start_date_latest',
  end_date: 'search.field.end_date',
  end_date_earliest: 'search.field.end_date_earliest',
  end_date_latest: 'search.field.end_date_latest',
  duration_months: 'search.field.duration_months',
  people_confirmed: 'search.field.people_confirmed',
  people_possible: 'search.field.people_possible',
  possible_join_date: 'search.field.possible_join_date',
  minor_occupant: 'search.field.minor_occupant',
  budget_amount: 'search.field.budget_amount',
  budget_ideal: 'search.field.budget_ideal',
  budget_max: 'search.field.budget_max',
  budget_scope: 'search.field.budget_scope',
  bills_scope: 'search.field.bills_scope',
  university: 'search.field.university',
  area: 'search.field.area',
  excluded_area: 'search.field.excluded_area',
  max_commute_minutes: 'search.field.max_commute_minutes',
  transport_mode: 'search.field.transport_mode',
  source_policy: 'search.field.source_policy',
  room_private_required: 'search.field.room_private_required',
  roommate_opt_in: 'search.field.roommate_opt_in',
  occupant_gender: 'search.field.occupant_gender',
  document_held: 'search.field.document_held',
  document_lacking: 'search.field.document_lacking',
  location: 'search.field.location',
}

export function fieldLabel(field: string): string {
  return label(FIELDS, field)
}

const FIELD_ORDER = Object.keys(FIELDS)

/**
 * Where a field sits in the list: what they want, when, who, how much, where.
 * The order the extractor happened to state them in is not an order anybody
 * reads in.
 */
export function fieldOrder(field: string): number {
  const at = FIELD_ORDER.indexOf(field)
  return at < 0 ? FIELD_ORDER.length : at
}

const FIELD_SOURCES: Record<string, MessageKey> = {
  client: 'search.source.client',
  manager: 'search.source.manager',
  ai: 'search.source.ai',
}

export function fieldSourceLabel(source: string): string {
  return label(FIELD_SOURCES, source)
}

const IMPORTANCE: Record<string, MessageKey> = {
  hard: 'search.importance.hard',
  soft: 'search.importance.soft',
}

/**
 * How firmly the client meant it. "800 maximum, strictly" is hard; the same
 * number offered loosely is soft, and the matcher treats them differently.
 */
export function importanceLabel(importance: string): string {
  return label(IMPORTANCE, importance)
}

const TERM_BANDS: Record<string, MessageKey> = {
  short: 'search.termBand.short',
  long: 'search.termBand.long',
}

export function termBandLabel(band: string): string {
  return label(TERM_BANDS, band)
}

const PRIORITIES: Record<string, MessageKey> = {
  low: 'tasks.priority.low',
  normal: 'tasks.priority.normal',
  high: 'tasks.priority.high',
  urgent: 'tasks.priority.urgent',
  critical: 'tasks.priority.critical',
}

export function priorityLabel(priority: string): string {
  return label(PRIORITIES, priority)
}

const HANDOFF_REASONS: Record<string, MessageKey> = {
  refund: 'tasks.reason.refund',
  discount: 'tasks.reason.discount',
  nonstandard_fee: 'tasks.reason.nonstandard_fee',
  payment: 'tasks.reason.payment',
  booking: 'tasks.reason.booking',
  signing: 'tasks.reason.signing',
  keys_handover: 'tasks.reason.keys_handover',
  contract: 'tasks.reason.contract',
  residenza: 'tasks.reason.residenza',
  complaint: 'tasks.reason.complaint',
  threat: 'tasks.reason.threat',
  fraud: 'tasks.reason.fraud',
  conflict: 'tasks.reason.conflict',
  unconfirmed_terms: 'tasks.reason.unconfirmed_terms',
  minor: 'tasks.reason.minor',
  sensitive_data: 'tasks.reason.sensitive_data',
  low_confidence: 'tasks.reason.low_confidence',
  contradiction: 'tasks.reason.contradiction',
  intent_unclear: 'tasks.reason.intent_unclear',
  undecided_policy: 'tasks.reason.undecided_policy',
  session_lost: 'tasks.reason.session_lost',
  flood_wait: 'tasks.reason.flood_wait',
  pipeline_failure: 'tasks.reason.pipeline_failure',
  budget_exhausted: 'tasks.reason.budget_exhausted',
  unrecognised: 'tasks.reason.unrecognised',
}

/**
 * Task kinds are free text (`ports.Task.Kind`), and the ones the pipeline raises
 * are spelled `handoff:<reason>`. The prefix is stripped so the manager reads
 * the reason, and an unrecognised kind is shown as it was stored.
 */
export function taskKindLabel(kind: string): string {
  const reason = kind.startsWith('handoff:') ? kind.slice('handoff:'.length) : kind
  const key = HANDOFF_REASONS[reason]
  return key === undefined ? kind : t(key)
}

const LISTING_STATUSES: Record<string, MessageKey> = {
  imported: 'listing.status.imported',
  contact_pending: 'listing.status.contact_pending',
  verified: 'listing.status.verified',
  stale: 'listing.status.stale',
  on_hold: 'listing.status.on_hold',
  reserved: 'listing.status.reserved',
  rented: 'listing.status.rented',
  withdrawn: 'listing.status.withdrawn',
  rejected: 'listing.status.rejected',
  duplicate: 'listing.status.duplicate',
}

export function listingStatusLabel(status: string): string {
  return label(LISTING_STATUSES, status)
}

const UNIT_KINDS: Record<string, MessageKey> = {
  apartment: 'unit.kind.apartment',
  room: 'unit.kind.room',
  bed: 'unit.kind.bed',
}

export function unitKindLabel(kind: string): string {
  return label(UNIT_KINDS, kind)
}

const PROPERTY_ROLES: Record<string, MessageKey> = {
  owner: 'owners.role.owner',
  agent: 'owners.role.agent',
  manager: 'owners.role.manager',
  current_tenant: 'owners.role.current_tenant',
  publisher: 'owners.role.publisher',
}

export function propertyRoleLabel(role: string): string {
  return label(PROPERTY_ROLES, role)
}

const SOURCE_TYPES: Record<string, MessageKey> = {
  private: 'listing.sourceType.private',
  agent: 'listing.sourceType.agent',
  organization: 'listing.sourceType.organization',
  unknown: 'listing.sourceType.unknown',
}

/**
 * Who is letting it. Unknown is its own answer, never "probably private": a
 * client who asked for owners only is refused an unknown source rather than
 * given the benefit of the doubt.
 */
export function sourceTypeLabel(type: string): string {
  return label(SOURCE_TYPES, type)
}

const PRICE_COMPONENTS: Record<string, MessageKey> = {
  rent: 'listing.component.rent',
  condominio: 'listing.component.condominio',
  fixed_bills: 'listing.component.fixed_bills',
  variable_bills: 'listing.component.variable_bills',
  other_recurring: 'listing.component.other_recurring',
  deposit: 'listing.component.deposit',
  first_month: 'listing.component.first_month',
  prepayment: 'listing.component.prepayment',
  agency_fee: 'listing.component.agency_fee',
  romaready_fee: 'listing.component.romaready_fee',
  one_time: 'listing.component.one_time',
}

export function priceComponentLabel(component: string): string {
  return label(PRICE_COMPONENTS, component)
}

const VERIFICATION_METHOD_LABELS: Record<string, MessageKey> = {
  owner_message: 'freshness.methodValue.owner_message',
  call: 'freshness.methodValue.call',
  source_page: 'freshness.methodValue.source_page',
  visit: 'freshness.methodValue.visit',
}

export function verificationMethodLabel(method: string): string {
  return label(VERIFICATION_METHOD_LABELS, method)
}

const VERIFICATION_FACT_LABELS: Record<string, MessageKey> = {
  availability: 'freshness.fact.availability',
  price: 'freshness.fact.price',
  conditions: 'freshness.fact.conditions',
  source: 'freshness.fact.source',
}

export function verificationFactLabel(fact: string): string {
  return label(VERIFICATION_FACT_LABELS, fact)
}

const CHANGE_FIELDS: Record<string, MessageKey> = {
  status: 'history.field.status',
  title: 'history.field.title',
  availability: 'history.field.availability',
  availability_windows: 'history.field.availability_windows',
  price_components: 'history.field.price_components',
  contract_type_declared: 'history.field.contract_type_declared',
  required_documents: 'history.field.required_documents',
  restrictions: 'history.field.restrictions',
  rent: 'listing.component.rent',
  condominio: 'listing.component.condominio',
  fixed_bills: 'listing.component.fixed_bills',
  variable_bills: 'listing.component.variable_bills',
  deposit: 'listing.component.deposit',
  agency_fee: 'listing.component.agency_fee',
}

/**
 * What moved. `listing_changes.field` is free text, so an unrecognised one is
 * shown as it was stored: a history row a manager cannot read is still a row
 * they can see happened, and inventing a name for it would be worse.
 */
export function changeFieldLabel(field: string): string {
  return label(CHANGE_FIELDS, field)
}

const CHANGE_SOURCES: Record<string, MessageKey> = {
  source: 'history.source.source',
  owner: 'history.source.owner',
  manager: 'history.source.manager',
  system: 'history.source.system',
}

/** Who noticed the change. A re-crawl and the owner saying so are not the same evidence. */
export function changeSourceLabel(source: string): string {
  return label(CHANGE_SOURCES, source)
}

// ---------- values inside a search request ----------

const UNITS: Record<string, MessageKey> = {
  apartment: 'search.value.unit.apartment',
  room: 'search.value.unit.room',
  bed: 'search.value.unit.bed',
  roommate_group: 'search.value.unit.roommate_group',
  specific_listing: 'search.value.unit.specific_listing',
}

/**
 * "room,bed" is every shape the client would take, so it reads as a choice:
 * «своя комната или место в комнате».
 */
export function unitTypesLabel(value: string): string {
  const shapes = value
    .split(',')
    .map((shape) => shape.trim())
    .filter((shape) => shape !== '')
  if (shapes.length === 0) return t('search.notSaid')
  return shapes.map((shape) => label(UNITS, shape)).join(t('search.value.or'))
}

const BUDGET_SCOPES: Record<string, MessageKey> = {
  per_person: 'search.value.budgetScope.per_person',
  party: 'search.value.budgetScope.party',
  total: 'search.value.budgetScope.total',
  unknown: 'search.value.budgetScope.unknown',
}

export function budgetScopeLabel(scope: string): string {
  return label(BUDGET_SCOPES, scope)
}

const BILLS_SCOPES: Record<string, MessageKey> = {
  included: 'search.value.billsScope.included',
  excluded: 'search.value.billsScope.excluded',
  unknown: 'search.value.billsScope.unknown',
}

export function billsScopeLabel(scope: string): string {
  return label(BILLS_SCOPES, scope)
}

const SOURCE_POLICIES: Record<string, MessageKey> = {
  private_only: 'search.value.sourcePolicy.private_only',
  agents_allowed: 'search.value.sourcePolicy.agents_allowed',
  no_agency_fee: 'search.value.sourcePolicy.no_agency_fee',
  unknown: 'search.value.sourcePolicy.unknown',
}

const ROOMMATES: Record<string, MessageKey> = {
  yes: 'search.value.roommate.yes',
  no: 'search.value.roommate.no',
  not_asked: 'search.value.roommate.not_asked',
}

const TRANSPORT: Record<string, MessageKey> = {
  walk: 'search.value.transport.walk',
  transit: 'search.value.transport.transit',
  bike: 'search.value.transport.bike',
  car: 'search.value.transport.car',
}

const GENDERS: Record<string, MessageKey> = {
  women: 'search.value.gender.women',
  men: 'search.value.gender.men',
  mixed: 'search.value.gender.mixed',
}

const DOCUMENTS: Record<string, MessageKey> = {
  passport: 'search.value.document.passport',
  codice_fiscale: 'search.value.document.codice_fiscale',
  permesso_di_soggiorno: 'search.value.document.permesso_di_soggiorno',
  employment_contract: 'search.value.document.employment_contract',
  enrolment_certificate: 'search.value.document.enrolment_certificate',
  guarantor: 'search.value.document.guarantor',
}

/**
 * A text value as a person says it. Areas, universities and anything this
 * build does not know are shown as the client wrote them.
 */
export function textValueLabel(field: string, value: string): string {
  switch (field) {
    case 'unit_type':
    case 'preferred_unit_type':
      return unitTypesLabel(value)
    case 'budget_scope':
      return budgetScopeLabel(value)
    case 'bills_scope':
      return billsScopeLabel(value)
    case 'source_policy':
      return label(SOURCE_POLICIES, value)
    case 'roommate_opt_in':
      return label(ROOMMATES, value)
    case 'transport_mode':
      return label(TRANSPORT, value)
    case 'occupant_gender':
      return label(GENDERS, value)
    case 'document_held':
    case 'document_lacking':
      return label(DOCUMENTS, value)
    default:
      return value
  }
}

const RISKS: Record<string, MessageKey> = {
  minor_occupant: 'search.risk.minor_occupant',
  field_contradiction: 'search.risk.field_contradiction',
  occupancy_may_change: 'search.risk.occupancy_may_change',
  dates_contradict: 'search.risk.dates_contradict',
  duration_disagrees_with_dates: 'search.risk.duration_disagrees_with_dates',
}

export function riskFlagLabel(flag: string): string {
  return label(RISKS, flag)
}

const CONFIDENCE_WORDS: Record<string, MessageKey> = {
  high: 'search.confidenceWord.high',
  medium: 'search.confidenceWord.medium',
  low: 'search.confidenceWord.low',
}

/** Confidence as three bands: the boundaries the gate uses to ask again. */
export function confidenceBand(confidence: number): 'high' | 'medium' | 'low' {
  if (confidence >= 0.8) return 'high'
  if (confidence >= 0.5) return 'medium'
  return 'low'
}

export function confidenceLabel(confidence: number): string {
  return label(CONFIDENCE_WORDS, confidenceBand(confidence))
}

// ---------- why a draft says what it says ----------

const QUESTIONS: Record<string, MessageKey> = {
  start_date: 'drafts.question.start_date',
  end_date_or_duration: 'drafts.question.end_date_or_duration',
  people_count: 'drafts.question.people_count',
  unit_type: 'drafts.question.unit_type',
  budget_amount: 'drafts.question.budget_amount',
  budget_scope: 'drafts.question.budget_scope',
  people_possible_join_date: 'drafts.question.people_possible_join_date',
  unit_type_preference: 'drafts.question.unit_type_preference',
  location: 'drafts.question.location',
  room_standard: 'drafts.question.room_standard',
  roommate_opt_in: 'drafts.question.roommate_opt_in',
  occupant_gender: 'drafts.question.occupant_gender',
  source_policy: 'drafts.question.source_policy',
}

const GATE_ACTIONS: Record<string, MessageKey> = {
  draft: 'drafts.reason.draft',
  handoff: 'drafts.reason.handoff',
  auto: 'drafts.reason.auto',
  ignore: 'drafts.reason.ignore',
}

/**
 * A reason code on a draft — `gate:draft`, `asks:start_date`,
 * `handoff:payment` — as a fragment a manager reads at a glance.
 */
export function reasonCodeLabel(code: string): string {
  const at = code.indexOf(':')
  const kind = at < 0 ? code : code.slice(0, at)
  const rest = at < 0 ? '' : code.slice(at + 1)
  switch (kind) {
    case 'gate':
      return label(GATE_ACTIONS, rest)
    case 'asks':
      return t('drafts.reason.asks', { what: label(QUESTIONS, rest) })
    case 'handoff':
      return t('drafts.reason.toPerson', { what: taskKindLabel(rest) })
    case 'composer_suggested_handoff':
      return t('drafts.reason.suggestedHandoff')
    default:
      return code
  }
}

/** Whether a reason code is one a manager should notice. */
export function reasonCodeTone(code: string): 'warning' | 'muted' {
  return code.startsWith('handoff:') || code === 'composer_suggested_handoff' ? 'warning' : 'muted'
}

const CHECKS: Record<string, MessageKey> = {
  outbound_enabled: 'drafts.check.outbound_enabled',
  draft_approved: 'drafts.check.draft_approved',
  thread_open: 'drafts.check.thread_open',
  account_matches: 'drafts.check.account_matches',
  peer_wrote_first: 'drafts.check.peer_wrote_first',
  session_healthy: 'drafts.check.session_healthy',
  nothing_newer: 'drafts.check.nothing_newer',
  listings_fresh: 'drafts.check.listings_fresh',
  photos_present: 'drafts.check.photos_present',
  within_rate_limit: 'drafts.check.within_rate_limit',
}

export function preflightCheckLabel(name: string): string {
  return label(CHECKS, name)
}

// ---------- why a task was raised ----------

const TASK_EXPLANATIONS: Record<string, MessageKey> = {
  refund: 'tasks.explain.refund',
  discount: 'tasks.explain.discount',
  nonstandard_fee: 'tasks.explain.nonstandard_fee',
  payment: 'tasks.explain.payment',
  booking: 'tasks.explain.booking',
  signing: 'tasks.explain.signing',
  keys_handover: 'tasks.explain.keys_handover',
  contract: 'tasks.explain.contract',
  residenza: 'tasks.explain.residenza',
  complaint: 'tasks.explain.complaint',
  threat: 'tasks.explain.threat',
  fraud: 'tasks.explain.fraud',
  conflict: 'tasks.explain.conflict',
  unconfirmed_terms: 'tasks.explain.unconfirmed_terms',
  minor: 'tasks.explain.minor',
  sensitive_data: 'tasks.explain.sensitive_data',
  low_confidence: 'tasks.explain.low_confidence',
  contradiction: 'tasks.explain.contradiction',
  intent_unclear: 'tasks.explain.intent_unclear',
  undecided_policy: 'tasks.explain.undecided_policy',
  session_lost: 'tasks.explain.session_lost',
  flood_wait: 'tasks.explain.flood_wait',
  pipeline_failure: 'tasks.explain.pipeline_failure',
  budget_exhausted: 'tasks.explain.budget_exhausted',
  unrecognised: 'tasks.explain.unrecognised',
}

/** One sentence on what this kind of task means, or nothing for a kind this build does not know. */
export function taskExplanation(kind: string): string | undefined {
  const reason = kind.startsWith('handoff:') ? kind.slice('handoff:'.length) : kind
  const key = TASK_EXPLANATIONS[reason]
  return key === undefined ? undefined : t(key)
}

const INTENTS: Record<string, MessageKey> = {
  money: 'tasks.intent.money',
  contract_legal: 'tasks.intent.contract_legal',
  complaint: 'tasks.intent.complaint',
}

/**
 * The concrete trigger of a task, in Russian.
 *
 * The server stores the reason as one English sentence — the register's summary
 * of the code, then " — " and what fired it — because it is also the audit
 * trail. The summary is replaced by taskExplanation; the trigger is translated
 * here when it has one of the shapes the rules write, and handed back as it is
 * otherwise (a pipeline failure's technical cause), marked so the screen can
 * put it out of the way.
 */
export function taskTrigger(reason: string): { readonly text: string; readonly translated: boolean } | undefined {
  const at = reason.indexOf(' — ')
  const detail = (at < 0 ? '' : reason.slice(at + 3)).trim()
  if (detail === '') return undefined

  const patterns: readonly [RegExp, (m: RegExpMatchArray) => string][] = [
    [/^the client wrote "(.+)"$/, (m) => t('tasks.detail.clientWrote', { phrase: m[1] ?? '' })],
    [/^the client says they are (\d+)$/, (m) => t('tasks.detail.clientAge', { age: m[1] ?? '' })],
    [/^our records say a minor is involved$/, () => t('tasks.detail.minorOnRecord')],
    [/^the classifier read the message as "(.+)"$/, (m) => t('tasks.detail.readAs', { intent: label(INTENTS, m[1] ?? '') })],
    [/^the classifier reported "(.+)"$/, (m) => t('tasks.detail.flagged', { flag: m[1] ?? '' })],
    [
      /^(\d+) clarifying questions in, confidence is still ([\d.]+)$/,
      (m) => t('tasks.detail.lowConfidence', { count: m[1] ?? '', confidence: m[2] ?? '' }),
    ],
    [/^\[(.*)\] still contradict themselves$/, (m) => t('tasks.detail.contradicts', { fields: (m[1] ?? '').split(' ').map(fieldLabel).join(', ') })],
    [
      /^(\d+) clarifying questions in, we still cannot tell what the client wants$/,
      (m) => t('tasks.detail.unclear', { count: m[1] ?? '' }),
    ],
    [/^a price or condition we already sent this client has changed$/, () => t('tasks.detail.termsChanged')],
    [/^the account is not authorised on Telegram$/, () => t('tasks.detail.notAuthorised')],
    [/^a step of the pipeline did not finish$/, () => t('tasks.detail.pipelineStep')],
    [/^today's spending on models has reached/, () => t('tasks.detail.budgetDay')],
    [/^this conversation has already been through the models/, () => t('tasks.detail.budgetThread')],
    [/^Telegram is holding this account off until (.+)$/, (m) => t('tasks.detail.floodUntil', { until: m[1] ?? '' })],
  ]

  // A phrase quoted after a summary, as the minor rule writes it:
  // "... (the client wrote "мой сын")".
  const quoted = /^(.*) \(the client wrote "(.+)"\)$/.exec(detail)
  for (const [pattern, render] of patterns) {
    const matched = pattern.exec(quoted?.[1] ?? detail)
    if (matched !== null) {
      const text = render(matched)
      return {
        text: quoted === null ? text : `${text}; ${t('tasks.detail.clientWrote', { phrase: quoted[2] ?? '' })}`,
        translated: true,
      }
    }
  }
  return { text: detail, translated: false }
}
